import { useEffect, useState } from 'react';
import type { DiscographyRelease } from '@tsuyosa/types';
import { RELEASE_TYPE_LABELS } from '@tsuyosa/types';
import { getPublicDiscography } from '@/lib/api';

export function DiscographyPage() {
  const [releases,  setReleases]  = useState<DiscographyRelease[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error,     setError]     = useState('');

  useEffect(() => {
    getPublicDiscography()
      .then(setReleases)
      .catch((err) => setError(err instanceof Error ? err.message : 'Gagal memuat data'))
      .finally(() => setIsLoading(false));
  }, []);

  function formatDate(date: string | null, precision: DiscographyRelease['date_precision']): string {
    if (!date) return '';
    const d = new Date(date);
    if (precision === 'year')  return String(d.getFullYear());
    if (precision === 'month') return d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
    return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <h1 className="mb-6 text-2xl font-bold text-white">Discography</h1>

      {isLoading && (
        <div className="flex justify-center py-20">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#f472b6] border-t-transparent" />
        </div>
      )}

      {error && (
        <p className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      )}

      {!isLoading && !error && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {releases.map((release) => (
            <div
              key={release.id}
              className="overflow-hidden rounded-xl border border-white/5 bg-[#130d1a] transition-colors hover:border-[#f472b6]/30"
            >
              <div className="aspect-square overflow-hidden bg-white/5">
                {release.cover_url ? (
                  <img
                    src={release.cover_url}
                    alt={release.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-5xl text-white/10">
                    ♪
                  </div>
                )}
              </div>
              <div className="p-4">
                <span className="inline-block rounded-full bg-[#f472b6]/10 px-2 py-0.5 text-[10px] font-medium text-[#f472b6]">
                  {RELEASE_TYPE_LABELS[release.type]}
                </span>
                <p className="mt-2 font-semibold text-white leading-snug">{release.title}</p>
                {release.release_date && (
                  <p className="mt-1 text-xs text-white/40">
                    {formatDate(release.release_date, release.date_precision)}
                  </p>
                )}
                {Object.keys(release.streaming_links).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {Object.entries(release.streaming_links).slice(0, 3).map(([platform, url]) => (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-white/50 hover:border-white/30 hover:text-white capitalize transition-colors"
                      >
                        {platform.replace('_', ' ')}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !error && releases.length === 0 && (
        <p className="py-20 text-center text-sm text-white/30">Belum ada rilis.</p>
      )}
    </div>
  );
}
